import type { IFilesystem, FileMetadata } from '@livx.cc/wcli/core';

/**
 * Hybrid / composite VFS — a root backend with other IFilesystems mounted at path
 * prefixes. Each op routes to the longest-matching mount and is translated into that
 * backend's OWN root space (`/skills/x.md` → the /skills mount's `/x.md`); everything
 * else falls through to the root backend. `readDir` merges the routed listing with
 * mount-point names at that level, and intermediate mount-ancestor dirs (e.g. `/vendor`
 * when only `/vendor/lib` is mounted) are synthesized.
 *
 * This is what lets you mix sources: edit a real project on disk at `/` while reading
 * skills/tools/docs from a bundled in-memory VFS or another disk folder — one filesystem
 * to the agent. Pure IFilesystem composition: edge-safe.
 */
const DIR_META: FileMetadata = { created: new Date(0), modified: new Date(0), size: 0, permissions: 'drwxr-xr-x', isExecutable: false };
const norm = (p: string) => '/' + p.split('/').filter(Boolean).join('/'); // leading slash, no trailing

export interface Mount { prefix: string; fs: IFilesystem }

export class MountFilesystem implements IFilesystem {
  private mounts: Mount[];

  constructor(private root: IFilesystem, mounts: Mount[] = []) {
    this.mounts = mounts
      .map((m) => ({ prefix: norm(m.prefix), fs: m.fs }))
      .sort((a, b) => b.prefix.length - a.prefix.length); // longest prefix wins
  }

  resolvePath(path: string, cwd?: string): string { return this.root.resolvePath(path, cwd ?? this.root.getCwd()); }
  getCwd(): string { return this.root.getCwd(); }
  setCwd(path: string): void { this.root.setCwd(path); }
  private abs(p: string): string { return this.resolvePath(p); }

  /** Route an absolute VFS path to a backend + its path within that backend. */
  private route(abs: string): { fs: IFilesystem; sub: string } {
    for (const m of this.mounts) {
      if (abs === m.prefix || abs.startsWith(m.prefix + '/')) return { fs: m.fs, sub: abs.slice(m.prefix.length) || '/' };
    }
    return { fs: this.root, sub: abs };
  }

  /** Is `abs` strictly ABOVE a mount point (a synthesized dir the root may not have)? */
  private isMountAncestor(abs: string): boolean {
    const base = abs === '/' ? '/' : abs + '/';
    return this.mounts.some((m) => m.prefix.startsWith(base));
  }

  /** Immediate child segment names of `abs` that come from mounts (for readDir merge). */
  private mountChildrenOf(abs: string): string[] {
    const base = abs === '/' ? '/' : abs + '/';
    const out = new Set<string>();
    for (const m of this.mounts) if (m.prefix.startsWith(base)) out.add(m.prefix.slice(base.length).split('/')[0]);
    return [...out];
  }

  async readFile(path: string): Promise<string> { const { fs, sub } = this.route(this.abs(path)); return fs.readFile(sub); }
  async writeFile(path: string, content: string): Promise<void> { const { fs, sub } = this.route(this.abs(path)); return fs.writeFile(sub, content); }
  async deleteFile(path: string): Promise<void> { const { fs, sub } = this.route(this.abs(path)); return fs.deleteFile(sub); }
  async createDir(path: string): Promise<void> { const { fs, sub } = this.route(this.abs(path)); return fs.createDir(sub); }

  async exists(path: string): Promise<boolean> {
    const a = this.abs(path);
    if (this.isMountAncestor(a)) return true;
    const { fs, sub } = this.route(a);
    return fs.exists(sub);
  }
  async isDirectory(path: string): Promise<boolean> {
    const a = this.abs(path);
    if (this.isMountAncestor(a)) return true; // synthesized ancestor dir
    const { fs, sub } = this.route(a);
    return fs.isDirectory(sub);
  }
  async isFile(path: string): Promise<boolean> {
    const a = this.abs(path);
    if (this.isMountAncestor(a)) return false;
    const { fs, sub } = this.route(a);
    return fs.isFile(sub);
  }
  async stat(path: string): Promise<FileMetadata> {
    const a = this.abs(path);
    if (this.isMountAncestor(a)) return DIR_META;
    const { fs, sub } = this.route(a);
    return fs.stat(sub);
  }

  async readDir(path: string): Promise<string[]> {
    const a = this.abs(path);
    const { fs, sub } = this.route(a);
    const names = new Set<string>();
    let routedOk = false;
    try { for (const n of await fs.readDir(sub)) names.add(n); routedOk = true; } catch { /* may be a pure synthetic ancestor */ }
    for (const n of this.mountChildrenOf(a)) names.add(n);
    if (!routedOk && !this.isMountAncestor(a)) throw new Error(`Directory not found: ${path}`);
    return [...names].sort();
  }
}
